import React, { useState } from "react";
import { 
  useSendPasswordResetEmailMutation,
  useVerifyPasswordResetCodeMutation,
  useResetPasswordConfirmMutation
} from "../../rtkq/services/userAuthApi";

export const PasswordResetFlow = ({ setForgotPasswordMode, resetForm }) => {
  const [passwordResetStep, setPasswordResetStep] = useState(1); // 1=email, 2=code, 3=new password
  const [resetEmail, setResetEmail] = useState("");
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [resetError, setResetError] = useState("");
  const [passwordResetCode, setPasswordResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [newPasswordError, setNewPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [sendResetEmail, { isLoading: isResetEmailLoading }] = useSendPasswordResetEmailMutation();
  const [verifyPasswordResetCode, { isLoading: isVerifyResetCodeLoading }] = useVerifyPasswordResetCodeMutation();
  const [resetPasswordConfirm, { isLoading: isResetPasswordConfirmLoading }] = useResetPasswordConfirmMutation();

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError("");
    
    try {
      const res = await sendResetEmail({ email: resetEmail });
      
      if (res.error) {
        console.log("Reset email error:", res.error);
        setResetError(res.error.data?.email?.[0] || res.error.data?.non_field_errors?.[0] || "Failed to send reset email");
        return;
      }
      
      if (res.data) {
        setResetEmailSent(true);
        setPasswordResetStep(2); // Move to verification code step
      }
    } catch (error) {
      console.error("Reset password error:", error);
      setResetError("Failed to send reset email. Please try again.");
    }
  };

  const handleVerifyResetCode = async (e) => {
    e.preventDefault();
    setResetError("");
    
    try {
      const res = await verifyPasswordResetCode({ 
        email: resetEmail, 
        verification_code: passwordResetCode 
      });
      
      if (res.error) {
        console.log("Reset code verification error:", res.error);
        setResetError(res.error.data?.non_field_errors?.[0] || "Invalid verification code");
        return;
      }
      
      if (res.data) {
        // Move to password reset step
        setPasswordResetStep(3);
      }
    } catch (error) {
      console.error("Reset code verification error:", error);
      setResetError("Failed to verify code. Please try again.");
    }
  };
  
  const handleResetPasswordConfirm = async (e) => {
    e.preventDefault();
    setResetError("");
    
    // Validate passwords
    if (newPassword !== confirmNewPassword) {
      setNewPasswordError("Passwords do not match");
      return;
    }
    
    if (newPassword.length < 8) {
      setNewPasswordError("Password must be at least 8 characters long");
      return;
    }
    
    try {
      const res = await resetPasswordConfirm({ 
        email: resetEmail, 
        verification_code: passwordResetCode,
        password: newPassword 
      });
      
      if (res.error) {
        console.log("Password reset error:", res.error);
        setNewPasswordError(res.error.data?.non_field_errors?.[0] || "Failed to reset password");
        return;
      }
      
      if (res.data) {
        alert("Password has been reset successfully!");
        // Reset to login form
        setForgotPasswordMode(false);
        resetForm();
      }
    } catch (error) {
      console.error("Password reset error:", error);
      setNewPasswordError("Failed to reset password. Please try again.");
    }
  };

  // Step 1: Email Form
  if (passwordResetStep === 1) {
    return (
      <form className="space-y-6" onSubmit={handleResetPassword}>
        <div className="text-center mb-4">
          <h3 className="text-lg font-medium text-gray-700">Reset Password</h3>
          <p className="text-sm text-gray-500 mt-1">
            Enter your email address and we'll send you a verification code to reset your password.
          </p>
        </div>
        
        <div>
          <label 
            htmlFor="reset_email" 
            className="block text-sm font-medium text-gray-700"
          >
            Email
          </label>
          <div className="mt-1">
            <input
              type="email"
              name="reset_email"
              id="reset_email"
              required
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 
                       focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="Enter your email"
            />
          </div>
        </div>
      
        {resetError && (
          <div className="text-red-600 text-sm text-center bg-red-50 p-2 rounded-md">
            {resetError}
          </div>
        )}
      
        <div>
          <button
            type="submit"
            disabled={isResetEmailLoading}
            className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white 
                      ${isResetEmailLoading ? 'bg-gray-400' : 'bg-red-600 hover:bg-red-700'} 
                      focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition duration-300`}
          >
            {isResetEmailLoading ? (
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              </div>
            ) : (
              'Send Verification Code'
            )}
          </button>
        </div>
      
        <div className="text-center">
          <button 
            type="button" 
            onClick={() => {
              setForgotPasswordMode(false);
              resetForm();
            }} 
            className="text-green-600 hover:text-green-500 font-medium text-sm"
          >
            Back to Login
          </button>
        </div>
      </form>
    );
  }
  
  // Step 2: Verification Code Form
  if (passwordResetStep === 2) {
    return (
      <form className="space-y-6" onSubmit={handleVerifyResetCode}>
        <div className="text-center mb-4">
          <h3 className="text-lg font-medium text-gray-700">Verify Code</h3>
          <p className="text-sm text-gray-500 mt-1">
            We've sent a verification code to {resetEmail}. 
            Please enter the code below to reset your password.
          </p>
        </div>
        
        <div>
          <label 
            htmlFor="password_reset_code" 
            className="block text-sm font-medium text-gray-700"
          >
            Verification Code
          </label>
          <div className="mt-1">
            <input
              type="text"
              name="password_reset_code"
              id="password_reset_code"
              required
              value={passwordResetCode}
              onChange={(e) => setPasswordResetCode(e.target.value)}
              className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 
                       focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="Enter verification code"
            />
          </div>
        </div>

        {resetError && (
          <div className="text-red-600 text-sm text-center bg-red-50 p-2 rounded-md">
            {resetError}
          </div>
        )}

        <div>
          <button
            type="submit"
            disabled={isVerifyResetCodeLoading}
            className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white 
                      ${isVerifyResetCodeLoading ? 'bg-gray-400' : 'bg-red-600 hover:bg-red-700'} 
                      focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition duration-300`}
          >
            {isVerifyResetCodeLoading ? (
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Verifying...
              </div>
            ) : (
              'Verify Code'
            )}
          </button>
        </div>
        
        <div className="text-center text-sm">
          <span className="text-gray-500">Didn't receive the code? </span>
          <button 
            type="button" 
            onClick={handleResetPassword} 
            className="text-green-600 hover:text-green-500 font-medium"
          >
            Resend
          </button>
        </div>
      </form>
    );
  }
  
  // Step 3: New Password Form
  return (
    <form className="space-y-6" onSubmit={handleResetPasswordConfirm}>
      <div className="text-center mb-4">
        <h3 className="text-lg font-medium text-gray-700">Set New Password</h3>
        <p className="text-sm text-gray-500 mt-1">
          Please enter your new password below.
        </p>
      </div>
      
      <div>
        <label 
          htmlFor="new_password" 
          className="block text-sm font-medium text-gray-700"
        >
          New Password
        </label>
        <div className="mt-1">
          <input
            type={showPassword ? "text" : "password"}
            name="new_password"
            id="new_password"
            required
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 
                     focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            placeholder="Enter new password (min. 8 characters)"
          />
        </div>
      </div>
      
      <div>
        <label 
          htmlFor="confirm_new_password" 
          className="block text-sm font-medium text-gray-700"
        >
          Confirm New Password
        </label>
        <div className="mt-1">
          <input
            type={showPassword ? "text" : "password"}
            name="confirm_new_password"
            id="confirm_new_password"
            required
            value={confirmNewPassword}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 
                     focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            placeholder="Confirm new password"
          />
        </div>
      </div>

      {newPasswordError && (
        <div className="text-red-600 text-sm text-center bg-red-50 p-2 rounded-md">
          {newPasswordError}
        </div>
      )}

      <div className="flex items-center">
        <input
          type="checkbox"
          id="showNewPassword"
          checked={showPassword}
          onChange={togglePasswordVisibility}
          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded cursor-pointer"
        />
        <label 
          htmlFor="showNewPassword" 
          className="ml-2 block text-sm text-gray-700 cursor-pointer"
        >
          Show Password
        </label>
      </div>

      <div>
        <button
          type="submit"
          disabled={isResetPasswordConfirmLoading}
          className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white 
                    ${isResetPasswordConfirmLoading ? 'bg-gray-400' : 'bg-red-600 hover:bg-red-700'} 
                    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition duration-300`}
        >
          {isResetPasswordConfirmLoading ? (
            <div className="flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Resetting...
            </div>
          ) : (
            'Reset Password'
          )}
        </button>
      </div>
    </form>
  );
};

export default PasswordResetFlow;
